import { useState, useEffect } from 'react'
import axios from 'axios'
import { JobCard } from './JobCard'
import { ApiService } from '../../../../api/services.gen'
import { useAlert } from '../../../../constants/AlertProvider'
import { SkeletonLoader } from '../../../common/SkeletonLoader'

export function JobList({ activeFilter, sortBy }) {
    const [jobs, setJobs] = useState([])
    const [companyCache, setCompanyCache] = useState({})
    const [isLoading, setIsLoading] = useState(true)
    const showAlert = useAlert()

    const mergeMatchData = (jobList, matches) => {
        const matchByJobId = new Map(matches.map((m) => [m.job, m]));
        return jobList.map((job) => {
            const match = matchByJobId.get(job.id);
            return {
                ...job,
                matchId: match?.id ?? null,
                isSaved: match?.is_saved ?? false,
                isGoodFit: match?.is_good_fit ?? false,
                isNotForMe: match?.is_not_for_me ?? false,
                matchScore: match ? Math.round(match.confidence_score * 100) : null,
            };
        });
    };

    const fetchJobs = () => {
        setIsLoading(true);
        Promise.all([
            ApiService.apiJobprofileList(),
            axios.get('/api/match-results/?match_type=job', { withCredentials: true }),
        ])
            .then(([jobRes, matchRes]) => {
                setJobs(mergeMatchData(jobRes.results || [], matchRes.data.results || matchRes.data || []));
                setIsLoading(false);
            })
            .catch(error => {
                showAlert("Error fetching jobs", "error");
                console.error("Error fetching jobs", error);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    const handleJobUpdated = (jobId, matchPatch) => {
        setJobs((prev) => prev.map((job) => (job.id === jobId ? { ...job, ...matchPatch } : job)));
    };

    useEffect(() => {
        const uniqueCompanyIds = [
            ...new Set(jobs.map(job => job.company).filter(Boolean))
        ].filter(id => !(id in companyCache));
        if (uniqueCompanyIds.length === 0) return;

        uniqueCompanyIds.forEach(id => {
            ApiService.apiCompanyRetrieve({ id })
                .then(company => {
                    setCompanyCache(prev => ({ ...prev, [id]: company }))
                })
                .catch(e => {
                    console.error("Error fetching company", id, e)
                })
        });
    }, [jobs, companyCache]);

    // Filter jobs based on active filter
    let filteredJobs = jobs
    if (activeFilter === 'saved') {
        filteredJobs = jobs.filter((job) => job.isSaved)
    }

    if (sortBy === 'match') {
        filteredJobs.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0))
    } else if (sortBy === 'recent') {
        filteredJobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    } else if (sortBy === 'salary') {
        filteredJobs.sort((a, b) => (b.salary_max || 0) - (a.salary_max || 0))
    }

    return (
        <div>
            {isLoading ? (
                // Show 3 skeleton cards while loading
                Array.from({ length: 3 }).map((_, idx) => <SkeletonLoader key={idx} />)
            ) : (
                filteredJobs.length > 0 ? (
                    filteredJobs.map((job) => <JobCard key={job.id} job={job} company={companyCache[job.company]} onJobUpdated={handleJobUpdated} />)
                ) : (
                    <div className="text-center py-10">
                        <p className="text-gray-500 dark:text-gray-400">No jobs found matching your criteria</p>
                    </div>
                )
            )}
        </div>
    )
}
